/**
 * A `Canvas` (`canvas` element): the commands its renderer recorded in Swift
 * (Sources/SwiftUI/API/Canvas.swift), drawn as SVG over the element's box and
 * clipped to it. SVG rather than a `<canvas>` so paints keep the page's CSS
 * colors (asset, semantic and accent colors follow the color scheme) and
 * images are the catalog's files as `<image>`s.
 *
 * Each command is a `<g>` (its opacity, blend mode, filters and clip) around
 * one element (its transform). An update reuses the elements in place, so a
 * canvas redrawn on every frame (a `TimelineView`) keeps its decoded images
 * instead of reloading them; only an image whose source changed is swapped.
 */

import type { AssetCatalog } from './assets';
import { paintFor, type IdSource } from './chartDom';
import { colorToCSS } from './styles';
import { renderSymbol } from './symbols';
import { resolveTextStyle, textStyleVar, DEFAULT_TYPE_SIZE, TEXT_STYLE_WEIGHT } from './typography';
import {
  isAssetImage,
  isBitmapImage,
  isUrlImage,
  type CanvasCommand,
  type CanvasFilter,
  type CanvasProps,
  type CanvasShading,
  type Font,
  type FontWeight,
  type GradientStop,
  type ImageProps,
  type ShapeStyle,
} from './protocol';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** The size a system symbol is drawn at without a rectangle (the body font's). */
const SYMBOL_SIZE = 20;
/** The body font's size, for text drawn without a font. */
const BODY_SIZE = 17;

const WEIGHTS: Record<FontWeight, string> = {
  ultraLight: '100',
  thin: '200',
  light: '300',
  regular: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  heavy: '800',
  black: '900',
};

let canvases = 0;

interface CanvasState {
  svg: SVGSVGElement;
  defs: SVGDefsElement;
  content: SVGGElement;
  prefix: string;
}

const STATE = new WeakMap<HTMLElement, CanvasState>();

/** The element of a `canvas`: a box that clips the SVG it holds. */
export function createCanvasElement(): HTMLElement {
  const el = document.createElement('div');
  el.className = 'sb-canvas';
  el.setAttribute('aria-hidden', 'true');
  return el;
}

/** Draws `props` into `el` (made by {@link createCanvasElement}). */
export function applyCanvas(el: HTMLElement, props: CanvasProps, assets: AssetCatalog): void {
  let state = STATE.get(el);
  if (!state) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    const defs = document.createElementNS(SVG_NS, 'defs');
    const content = document.createElementNS(SVG_NS, 'g');
    svg.append(defs, content);
    el.append(svg);
    state = { svg, defs, content, prefix: `sb-canvas${++canvases}` };
    STATE.set(el, state);
  }
  const width = finite(props.width, 0);
  const height = finite(props.height, 0);
  state.svg.setAttribute('width', String(width));
  state.svg.setAttribute('height', String(height));
  state.svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
  state.defs.replaceChildren();
  const ids: IdSource = { next: 0, prefix: state.prefix };
  const box = { x: 0, y: 0, width, height };
  const commands = Array.isArray(props.commands) ? props.commands : [];
  const groups = state.content.children;
  let index = 0;
  for (const command of commands) {
    if (!command || typeof command !== 'object') continue;
    const tag = tagFor(command, assets);
    if (!tag) continue;
    let group = groups[index] as SVGGElement | undefined;
    let shape = group?.firstElementChild as SVGElement | null | undefined;
    if (!group || !shape || shape.localName !== tag) {
      const fresh = document.createElementNS(SVG_NS, 'g');
      shape = tag === 'svg' ? null : document.createElementNS(SVG_NS, tag);
      if (shape) fresh.append(shape);
      if (group) group.replaceWith(fresh);
      else state.content.append(fresh);
      group = fresh;
    }
    const drawn = draw(group, shape ?? null, command, assets, state.defs, ids, box);
    applyState(group, drawn, command, state.defs, ids);
    index++;
  }
  while (groups.length > index) groups[groups.length - 1]!.remove();
}

/** The SVG element a command draws with; null for what is not drawn (a bitmap, a missing image). */
function tagFor(command: CanvasCommand, assets: AssetCatalog): string | null {
  switch (command.op) {
    case 'fill':
    case 'stroke':
      return 'path';
    case 'text':
      return 'text';
    case 'image': {
      const image = command.image;
      if (!image || typeof image !== 'object' || isBitmapImage(image)) return null;
      if (isUrlImage(image)) return 'image';
      if (isAssetImage(image)) return assets.images[image.name] ? 'image' : null;
      return 'svg';
    }
    default:
      return null;
  }
}

/** Draws `command` with `shape` (made anew for a symbol); returns the element drawn. */
function draw(
  group: SVGGElement,
  shape: SVGElement | null,
  command: CanvasCommand,
  assets: AssetCatalog,
  defs: SVGDefsElement,
  ids: IdSource,
  box: { x: number; y: number; width: number; height: number },
): SVGElement {
  switch (command.op) {
    case 'fill': {
      const path = shape!;
      setAttr(path, 'd', command.path ?? '');
      path.style.fill = paint(command.shading, defs, ids, box);
      path.style.stroke = 'none';
      setAttr(path, 'fill-rule', command.evenOdd ? 'evenodd' : null);
      clearStroke(path);
      return path;
    }
    case 'stroke': {
      const path = shape!;
      setAttr(path, 'd', command.path ?? '');
      path.style.fill = 'none';
      path.style.stroke = paint(command.shading, defs, ids, box);
      setAttr(path, 'fill-rule', null);
      setAttr(path, 'stroke-width', String(finite(command.lineWidth, 1)));
      setAttr(path, 'stroke-linecap', command.lineCap ?? 'butt');
      setAttr(path, 'stroke-linejoin', command.lineJoin ?? 'miter');
      setAttr(path, 'stroke-miterlimit', String(finite(command.miterLimit, 10)));
      const dash = Array.isArray(command.dash) && command.dash.length > 0 ? command.dash.join(' ') : null;
      setAttr(path, 'stroke-dasharray', dash);
      setAttr(path, 'stroke-dashoffset', dash ? String(finite(command.dashPhase, 0)) : null);
      return path;
    }
    case 'text': {
      const text = shape!;
      if (text.textContent !== command.text) text.textContent = command.text ?? '';
      setAttr(text, 'x', String(finite(command.x, 0)));
      setAttr(text, 'y', String(finite(command.y, 0)));
      const ax = finite(command.anchor?.x, 0.5);
      const ay = finite(command.anchor?.y, 0.5);
      setAttr(text, 'text-anchor', ax < 0.25 ? 'start' : ax > 0.75 ? 'end' : 'middle');
      setAttr(text, 'dominant-baseline', ay < 0.25 ? 'text-before-edge' : ay > 0.75 ? 'text-after-edge' : 'central');
      text.style.fill = paint(command.shading, defs, ids, box);
      applyFont(text, command.font);
      return text;
    }
    case 'image':
      return drawImage(group, shape, command, assets, defs, ids, box);
  }
}

function drawImage(
  group: SVGGElement,
  shape: SVGElement | null,
  command: Extract<CanvasCommand, { op: 'image' }>,
  assets: AssetCatalog,
  defs: SVGDefsElement,
  ids: IdSource,
  box: { x: number; y: number; width: number; height: number },
): SVGElement {
  const size = imageSize(command.image, assets);
  const width = finite(command.width, size.width);
  const height = finite(command.height, size.height);
  const x = finite(command.x, 0) - finite(command.anchor?.x, 0.5) * width;
  const y = finite(command.y, 0) - finite(command.anchor?.y, 0.5) * height;
  if (shape === null || shape.localName === 'svg') {
    // A system symbol: its glyph, painted with the shading (else the foreground).
    const name = (command.image as { systemName?: string }).systemName ?? '';
    if (shape === null || shape.dataset['sbSymbol'] !== name) {
      const glyph = renderSymbol(name) ?? document.createElementNS(SVG_NS, 'svg');
      glyph.dataset['sbSymbol'] = name;
      group.replaceChildren(glyph);
      shape = glyph;
    }
    shape.style.color = command.shading ? paint(command.shading, defs, ids, box) : colorToCSS({ name: 'primary' });
  }
  setAttr(shape, 'x', String(x));
  setAttr(shape, 'y', String(y));
  setAttr(shape, 'width', String(Math.max(0, width)));
  setAttr(shape, 'height', String(Math.max(0, height)));
  if (shape.localName === 'image') {
    setAttr(shape, 'preserveAspectRatio', 'none');
    const src = imageSource(command.image, assets);
    if (src && shape.getAttribute('href') !== src) shape.setAttribute('href', src);
  }
  return shape;
}

function imageSize(image: ImageProps, assets: AssetCatalog): { width: number; height: number } {
  if (isUrlImage(image)) return { width: finite(image.width, 0), height: finite(image.height, 0) };
  if (isAssetImage(image)) {
    const asset = assets.images[image.name];
    return { width: finite(asset?.width, 0), height: finite(asset?.height, 0) };
  }
  return { width: SYMBOL_SIZE, height: SYMBOL_SIZE };
}

function imageSource(image: ImageProps, assets: AssetCatalog): string | null {
  if (isUrlImage(image)) return image.url;
  if (isAssetImage(image)) return assets.images[image.name]?.src ?? null;
  return null;
}

/** The context state of `command`: transform on the shape; the rest on its group. */
function applyState(group: SVGGElement, shape: SVGElement, command: CanvasCommand, defs: SVGDefsElement, ids: IdSource): void {
  const t = command.transform;
  setAttr(shape, 'transform', Array.isArray(t) && t.length === 6 ? `matrix(${t.map((n) => finite(n, 0)).join(' ')})` : null);
  const opacity = command.opacity;
  group.style.opacity = opacity != null && opacity < 1 ? String(Math.max(0, opacity)) : '';
  group.style.mixBlendMode = command.blendMode && command.blendMode !== 'normal' ? command.blendMode : '';
  group.style.filter = filterCSS(command.filters);
  let clip: string | null = null;
  for (const d of Array.isArray(command.clips) ? command.clips : []) {
    const id = `${ids.prefix}-clip${ids.next++}`;
    const clipPath = document.createElementNS(SVG_NS, 'clipPath');
    clipPath.id = id;
    // A clip within the previous one: the intersection.
    if (clip) clipPath.setAttribute('clip-path', `url(#${clip})`);
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', d);
    clipPath.append(path);
    defs.append(clipPath);
    clip = id;
  }
  setAttr(group, 'clip-path', clip ? `url(#${clip})` : null);
}

function filterCSS(filters: CanvasFilter[] | undefined): string {
  if (!Array.isArray(filters)) return '';
  const parts: string[] = [];
  for (const filter of filters) {
    if (!filter || typeof filter !== 'object') continue;
    if ('blur' in filter) parts.push(`blur(${finite(filter.blur, 0)}px)`);
    else if ('shadow' in filter) {
      const { color, radius, x, y } = filter.shadow;
      parts.push(`drop-shadow(${finite(x, 0)}px ${finite(y, 0)}px ${finite(radius, 0)}px ${colorToCSS(color)})`);
    } else if ('grayscale' in filter) parts.push(`grayscale(${finite(filter.grayscale, 0)})`);
    else if ('saturation' in filter) parts.push(`saturate(${finite(filter.saturation, 1)})`);
    // SwiftUI's brightness adds to each component (0 leaves it as is).
    else if ('brightness' in filter) parts.push(`brightness(${1 + finite(filter.brightness, 0)})`);
    else if ('contrast' in filter) parts.push(`contrast(${finite(filter.contrast, 1)})`);
    else if ('hueRotation' in filter) parts.push(`hue-rotate(${finite(filter.hueRotation, 0)}deg)`);
    else if ('invert' in filter) parts.push(`invert(${finite(filter.invert, 1)})`);
  }
  return parts.join(' ');
}

/** The SVG paint of a shading; a canvas gradient is in the shape's own coordinates. */
function paint(shading: CanvasShading | undefined, defs: SVGDefsElement, ids: IdSource, box: { x: number; y: number; width: number; height: number }): string {
  if (!shading || typeof shading !== 'object') return colorToCSS({ name: 'primary' });
  if ('canvasLinearGradient' in shading) {
    const { stops, start, end } = shading.canvasLinearGradient;
    const gradient = document.createElementNS(SVG_NS, 'linearGradient');
    setAttrs(gradient, {
      gradientUnits: 'userSpaceOnUse',
      x1: finite(start?.x, 0),
      y1: finite(start?.y, 0),
      x2: finite(end?.x, 0),
      y2: finite(end?.y, 0),
    });
    return addGradient(gradient, stops, defs, ids);
  }
  if ('canvasRadialGradient' in shading) {
    const { stops, center, startRadius, endRadius } = shading.canvasRadialGradient;
    const gradient = document.createElementNS(SVG_NS, 'radialGradient');
    setAttrs(gradient, {
      gradientUnits: 'userSpaceOnUse',
      cx: finite(center?.x, 0),
      cy: finite(center?.y, 0),
      r: Math.max(0, finite(endRadius, 0)),
      fr: Math.max(0, finite(startRadius, 0)),
    });
    return addGradient(gradient, stops, defs, ids);
  }
  return paintFor(document, defs, shading as ShapeStyle, box, ids);
}

function addGradient(gradient: SVGElement, stops: GradientStop[] | undefined, defs: SVGDefsElement, ids: IdSource): string {
  const id = `${ids.prefix}-grad${ids.next++}`;
  gradient.id = id;
  const valid = (Array.isArray(stops) ? stops : []).filter((stop) => stop && typeof stop === 'object' && stop.color);
  const colors = valid.length === 0 ? [] : valid.length === 1 ? [valid[0]!, { ...valid[0]!, location: 1 }] : valid;
  for (const stop of colors) {
    const el = document.createElementNS(SVG_NS, 'stop');
    el.setAttribute('offset', String(Math.min(1, Math.max(0, finite(stop.location, 0)))));
    el.style.stopColor = colorToCSS(stop.color);
    gradient.append(el);
  }
  defs.append(gradient);
  return `url(#${id})`;
}

function applyFont(text: SVGElement, font: Font | undefined): void {
  const s = text.style;
  s.fontSize = `${BODY_SIZE}px`;
  s.fontWeight = '';
  if (!font || typeof font !== 'object') return;
  if (font.size != null) s.fontSize = `${finite(font.size, BODY_SIZE)}px`;
  else if (font.textStyle) {
    s.fontSize = `var(${textStyleVar(font.textStyle, 'size')}, ${resolveTextStyle(font.textStyle, DEFAULT_TYPE_SIZE).fontSize}px)`;
    const implied = TEXT_STYLE_WEIGHT[font.textStyle];
    if (!font.weight && implied) s.fontWeight = WEIGHTS[implied];
  }
  if (font.weight) s.fontWeight = WEIGHTS[font.weight] ?? '400';
}

function clearStroke(path: SVGElement): void {
  for (const name of ['stroke-width', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-dasharray', 'stroke-dashoffset']) {
    path.removeAttribute(name);
  }
}

function setAttr(el: Element, name: string, value: string | null): void {
  if (value == null) el.removeAttribute(name);
  else if (el.getAttribute(name) !== value) el.setAttribute(name, value);
}

function setAttrs(el: Element, attrs: Record<string, string | number>): void {
  for (const [name, value] of Object.entries(attrs)) el.setAttribute(name, String(value));
}

function finite(value: number | undefined | null, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
